import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useId, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { hasPasskey, PASSKEY_CHOICE } from '@/lib/auth';

const MAX_REASON = 500;

/**
 * Asks why before something that is logged with its reason. Confirm stays
 * blocked until the reason is long enough, with the missing piece spelled
 * out beneath it. A failure stays in the dialog and the draft is kept.
 * Ctrl/Cmd+Enter submits.
 */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pending,
  error,
  onConfirm,
  minLength = 8,
  confirmWord,
  askCode = false,
  allowPasskey = true,
  askReason = true,
  tone = 'default',
  children,
}: Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onConfirm: (reason: string, code: string) => void;
  /** The fewest characters the server accepts; 8 unless an action asks for more. */
  minLength?: number;
  /** When set, the person must type this word before confirm works (the highest step of the ladder). */
  confirmWord?: string;
  /** When set, the person also enters a fresh code from their authenticator app. */
  askCode?: boolean;
  /** Off where the server takes only an authenticator code typed just now. */
  allowPasskey?: boolean;
  /** Off for a change only the super admin can make: the dialog then asks for the code alone, and the reason is empty. */
  askReason?: boolean;
  tone?: 'default' | 'destructive';
  /** What the change does, shown under the description and above the fields. */
  children?: ReactNode;
}>) {
  const reasonId = useId();
  const hintId = useId();
  const wordId = useId();
  const codeId = useId();
  const [reason, setReason] = useState('');
  const [typedWord, setTypedWord] = useState('');
  const [code, setCode] = useState('');
  const [usePasskey, setUsePasskey] = useState(false);
  const passkey = useQuery({
    queryKey: ['staff-has-passkey'],
    queryFn: hasPasskey,
    enabled: open && askCode && allowPasskey,
    staleTime: 60_000,
  });
  const offerPasskey = askCode && allowPasskey && passkey.data === true;
  const trimmed = reason.trim();
  const wordOk = !confirmWord || typedWord.trim().toUpperCase() === confirmWord;
  const codeOk = !askCode || usePasskey || /^\d{6}$/.test(code.trim());
  const reasonOk =
    !askReason || (trimmed.length >= minLength && trimmed.length <= MAX_REASON);
  const valid = reasonOk && wordOk && codeOk;
  const missing = minLength - trimmed.length;

  function submit() {
    if (valid && !pending) {
      onConfirm(trimmed, usePasskey ? PASSKEY_CHOICE : code.trim());
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        {askReason ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor={reasonId}>Reason</Label>
            <Textarea
              id={reasonId}
              aria-describedby={hintId}
              value={reason}
              maxLength={MAX_REASON}
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  submit();
                }
              }}
            />
            <p
              id={hintId}
              className="flex justify-between gap-3 text-xs text-muted-foreground"
            >
              <span>
                {minLength} to {MAX_REASON} characters. It is kept in the audit
                log.
              </span>
              <span className="tabular-nums">
                {trimmed.length} / {MAX_REASON}
              </span>
            </p>
          </div>
        ) : null}
        {confirmWord ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor={wordId}>Type {confirmWord} to confirm</Label>
            <Input
              id={wordId}
              autoComplete="off"
              value={typedWord}
              onChange={(event) => setTypedWord(event.target.value)}
            />
          </div>
        ) : null}
        {askCode ? (
          <div className="flex flex-col gap-2">
            {usePasskey ? (
              <p className="text-sm">
                You will confirm with your passkey when you press {confirmLabel}
                .
              </p>
            ) : (
              <>
                <Label htmlFor={codeId}>Authenticator code</Label>
                <Input
                  id={codeId}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6-digit code"
                  className="w-full sm:w-44"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </>
            )}
            {offerPasskey ? (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                onClick={() => setUsePasskey(!usePasskey)}
              >
                {usePasskey
                  ? 'Use an authenticator code instead'
                  : 'Use my passkey instead'}
              </Button>
            ) : null}
          </div>
        ) : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <DialogFooter>
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant={tone === 'destructive' ? 'destructive' : 'default'}
            pending={pending}
            disabled={!valid}
            onClick={submit}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
        {askReason && !valid && trimmed.length > 0 ? (
          <p className="-mt-2 text-end text-xs text-muted-foreground">
            {missing > 0
              ? `Add ${missing} more ${missing === 1 ? 'character' : 'characters'} to continue.`
              : null}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
